import { z } from 'zod';
import { PURCHASE_ORDER_STATUSES } from '../enums.js';
import {
  optionalDate,
  optionalText,
  paginationQuerySchema,
  toPatchSchema,
  uuidSchema,
} from './common.js';
import { currencySchema } from './organization.js';
import { businessManagerFilterSchema, type BusinessManagerRef, type RecordRef } from './records.js';

/**
 * Purchase orders: what the organisation asks a vendor to supply.
 *
 * Lines are free text, typed on the order — never picked from a selling price
 * list, because what is bought is not what is sold. The totals are computed by
 * the server from the lines with the same engine as every other document, and
 * the order is never an expense by itself: an expense is created from it, once,
 * when the vendor's own bill is in.
 */

const money = z.coerce.number().min(0).max(1e15);
const rate = z.coerce.number().min(0).max(100);
/** Two decimals, like every other quantity: half a day of labour is a line. */
const quantity = z.coerce.number().min(0.01).max(1_000_000);

export const purchaseOrderLineSchema = z.object({
  description: z.string().trim().min(1, 'Required').max(4000),
  quantity: quantity.default(1),
  unitPrice: money.default(0),
  discountPercent: rate.default(0),
  taxRate: rate.default(0),
});
export type PurchaseOrderLineInput = z.infer<typeof purchaseOrderLineSchema>;

export const createPurchaseOrderSchema = z.object({
  vendorId: uuidSchema,
  subject: z.string().trim().min(1, 'Required').max(300),
  orderDate: z.iso.date(),
  expectedDate: optionalDate(),
  /** Defaults to the vendor's own currency, then the organisation's. */
  currency: currencySchema.optional(),
  lineItems: z.array(purchaseOrderLineSchema).max(200).optional(),
  notes: optionalText(8000),
  terms: optionalText(8000),
  businessManagerId: uuidSchema.optional(),
});
export type CreatePurchaseOrderInput = z.infer<typeof createPurchaseOrderSchema>;

/** The vendor, the currency and the lines are fixed once the order has been sent. */
export const updatePurchaseOrderSchema = toPatchSchema(createPurchaseOrderSchema);
export type UpdatePurchaseOrderInput = z.infer<typeof updatePurchaseOrderSchema>;

/** Recording what the vendor sent back: their quotation or invoice. */
export const recordVendorResponseSchema = z.object({
  vendorReference: z.string().trim().min(1, 'Required').max(120),
  vendorDocumentDate: optionalDate(),
  /** What the vendor actually billed, which may differ from the order total. */
  vendorBilledAmount: money.refine((value) => value > 0, 'Enter an amount greater than zero'),
  vendorResponseNotes: optionalText(4000),
});
export type RecordVendorResponseInput = z.infer<typeof recordVendorResponseSchema>;

/** Goods or services received: some of them, or all of them. */
export const receivePurchaseOrderSchema = z.object({
  complete: z.boolean(),
});
export type ReceivePurchaseOrderInput = z.infer<typeof receivePurchaseOrderSchema>;

export const cancelPurchaseOrderSchema = z.object({
  reason: z.string().trim().min(1, 'Say why it is being cancelled').max(500),
});
export type CancelPurchaseOrderInput = z.infer<typeof cancelPurchaseOrderSchema>;

/**
 * The expense a purchase order becomes, with the vendor's actual billed
 * amounts. The amount defaults to what was recorded from the vendor's reply.
 */
export const createExpenseFromPurchaseOrderSchema = z.object({
  expenseDate: z.iso.date(),
  amount: money.refine((value) => value > 0, 'Enter an amount greater than zero').optional(),
  category: z.string().trim().min(1).max(80).nullish(),
  bankAccountId: uuidSchema.nullish(),
  notes: optionalText(2000),
});
export type CreateExpenseFromPurchaseOrderInput = z.infer<
  typeof createExpenseFromPurchaseOrderSchema
>;

export const PURCHASE_ORDER_SORT_FIELDS = [
  'createdAt',
  'updatedAt',
  'orderDate',
  'total',
  'status',
] as const;

export const listPurchaseOrdersQuerySchema = paginationQuerySchema.extend({
  sortBy: z.enum(PURCHASE_ORDER_SORT_FIELDS).default('createdAt'),
  businessManagerId: businessManagerFilterSchema.optional(),
  vendorId: uuidSchema.optional(),
  status: z.enum(PURCHASE_ORDER_STATUSES).optional(),
  /** Only orders still waiting on the vendor, or on being turned into an expense. */
  openOnly: z.stringbool().optional(),
  from: z.iso.date().optional(),
  to: z.iso.date().optional(),
});
export type ListPurchaseOrdersQuery = z.infer<typeof listPurchaseOrdersQuerySchema>;

export interface PurchaseOrderLineView {
  id: string;
  description: string;
  quantity: string;
  unitPrice: string;
  discountPercent: string;
  taxRate: string;
  lineTotal: string;
  position: number;
}

export interface PurchaseOrderSummary {
  id: string;
  number: string;
  status: (typeof PURCHASE_ORDER_STATUSES)[number];
  subject: string;
  vendor: RecordRef;
  orderDate: string;
  expectedDate: string | null;
  currency: string;
  baseCurrency: string;
  exchangeRate: string;
  exchangeRateAt: string;
  exchangeRateSource: string;
  baseTotal: string;
  subtotal: string;
  discountAmount: string;
  taxAmount: string;
  total: string;
  /** The live expense created from this order, if any. */
  expense: RecordRef | null;
  businessManager: BusinessManagerRef | null;
  createdAt: string;
  updatedAt: string;
}

export interface PurchaseOrderDetail extends PurchaseOrderSummary {
  notes: string | null;
  terms: string | null;
  sentAt: string | null;
  vendorReference: string | null;
  vendorDocumentDate: string | null;
  vendorBilledAmount: string | null;
  vendorResponseNotes: string | null;
  vendorRespondedAt: string | null;
  completedAt: string | null;
  cancelledAt: string | null;
  cancellationReason: string | null;
  lineItems: PurchaseOrderLineView[];
}

export interface PurchaseOrderAttachmentView {
  id: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  uploadedAt: string;
}
